import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { ArrowLeft, CheckCircle, Clock, ExternalLink, RefreshCw, Wrench } from 'lucide-react';
import toast from 'react-hot-toast';
import { ordersAPI } from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';

type ItemStatus = 'pending' | 'received' | 'in_progress' | 'ready' | 'out_for_delivery' | 'completed' | 'cancelled' | 'issue_reported';

interface JobItem {
  _id: string;
  serviceId?: { name?: string; code?: string } | null;
  isCustomItem?: boolean;
  customName?: string;
  quantity: number;
  status: ItemStatus;
  specialInstructions?: string;
}

interface Job {
  _id: string;
  orderNumber: string;
  status: string;
  priority: string;
  dueDate?: string;
  customerId?: { firstName?: string; lastName?: string; phone?: string } | null;
  items: JobItem[];
  completedItems: number;
  openItems: number;
  jobProgress: 'pending' | 'partially_completed';
}

const itemStatuses: { value: ItemStatus; label: string }[] = [
  { value: 'pending', label: 'Pending' },
  { value: 'received', label: 'Received' },
  { value: 'in_progress', label: 'In Progress' },
  { value: 'ready', label: 'Ready for Collection/Delivery' },
  { value: 'out_for_delivery', label: 'Out for Delivery' },
  { value: 'completed', label: 'Completed' },
  { value: 'cancelled', label: 'Cancelled' },
  { value: 'issue_reported', label: 'Issue Reported' },
];

const PendingJobs: React.FC = () => {
  const navigate = useNavigate();
  const { user, activeSiteName } = useAuth();
  const [jobs, setJobs] = useState<Job[]>([]);
  const [summary, setSummary] = useState({ total: 0, pending: 0, partiallyCompleted: 0 });
  const [filter, setFilter] = useState<'all' | 'pending' | 'partially_completed'>('all');
  const [loading, setLoading] = useState(true);
  const [updatingItemId, setUpdatingItemId] = useState<string | null>(null);

  const loadJobs = async () => {
    setLoading(true);
    try {
      const response = await ordersAPI.getPendingJobs();
      setJobs(response.data.jobs || []);
      setSummary(response.data.summary || { total: 0, pending: 0, partiallyCompleted: 0 });
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to load pending jobs');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadJobs(); }, []);

  const updateItemStatus = async (job: Job, item: JobItem, status: ItemStatus) => {
    setUpdatingItemId(item._id);
    try {
      await ordersAPI.updateOrderItemStatus(job._id, item._id, status);
      toast.success('Item status updated');
      await loadJobs();
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update item status');
    } finally {
      setUpdatingItemId(null);
    }
  };

  const visibleJobs = jobs.filter(job => filter === 'all' || job.jobProgress === filter);
  const homePath = user?.role === 'admin' || user?.role === 'super_admin' ? '/admin' : '/pos';
  const dueLabel = (date?: string) => date ? new Date(date).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : 'No collection date';

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="bg-white border-b border-gray-200 px-4 sm:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <button onClick={() => navigate(homePath)} className="w-10 h-10 rounded-lg flex items-center justify-center bg-gray-100 hover:bg-gray-200" aria-label="Back">
              <ArrowLeft className="w-5 h-5 text-gray-600" />
            </button>
            <div className="w-10 h-10 bg-amber-100 rounded-lg flex items-center justify-center"><Wrench className="w-5 h-5 text-amber-700" /></div>
            <div>
              <h1 className="text-xl font-bold text-gray-900">Pending Jobs</h1>
              <p className="text-sm text-gray-500">{activeSiteName || 'Current site'} · update work as it progresses</p>
            </div>
          </div>
          <button onClick={loadJobs} disabled={loading} className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-300 rounded-lg text-sm font-medium hover:bg-gray-50 disabled:opacity-50">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} /> Refresh
          </button>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 py-6 space-y-5">
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          {[
            { key: 'all' as const, label: 'Open Jobs', value: summary.total, selectedClass: 'border-blue-500' },
            { key: 'pending' as const, label: 'Pending', value: summary.pending, selectedClass: 'border-gray-500' },
            { key: 'partially_completed' as const, label: 'Partially Completed', value: summary.partiallyCompleted, selectedClass: 'border-amber-500' },
          ].map(card => (
            <button key={card.key} onClick={() => setFilter(card.key)} className={`text-left p-4 bg-white rounded-xl border-2 transition-colors ${filter === card.key ? card.selectedClass : 'border-gray-200 hover:border-gray-300'}`}>
              <div className="text-sm text-gray-500">{card.label}</div>
              <div className="text-2xl font-bold text-gray-900 mt-1">{card.value}</div>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-16 text-center text-gray-500"><RefreshCw className="w-7 h-7 mx-auto animate-spin mb-3" />Loading jobs…</div>
        ) : visibleJobs.length === 0 ? (
          <div className="bg-white border border-gray-200 rounded-xl py-16 text-center">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
            <h2 className="text-lg font-semibold text-gray-900">No jobs in this queue</h2>
            <p className="text-sm text-gray-500 mt-1">Everything is up to date.</p>
          </div>
        ) : visibleJobs.map(job => (
          <section key={job._id} className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
            <div className="px-4 sm:px-5 py-4 border-b border-gray-200 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
              <div>
                <div className="flex items-center gap-2 flex-wrap">
                  <Link to={`/orders/${job._id}`} className="font-bold text-primary-700 hover:text-primary-900 inline-flex items-center gap-1">{job.orderNumber}<ExternalLink className="w-3.5 h-3.5" /></Link>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${job.jobProgress === 'partially_completed' ? 'bg-amber-100 text-amber-800' : 'bg-gray-100 text-gray-700'}`}>
                    {job.jobProgress === 'partially_completed' ? 'Partially completed' : 'Pending'}
                  </span>
                  {job.priority !== 'standard' && <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-700">{job.priority}</span>}
                </div>
                <p className="text-sm text-gray-500 mt-1">{job.customerId ? `${job.customerId.firstName || ''} ${job.customerId.lastName || ''}`.trim() : 'Unknown customer'}{job.customerId?.phone ? ` · ${job.customerId.phone}` : ''}</p>
              </div>
              <div className="sm:text-right">
                <div className="text-sm font-medium text-gray-700 flex items-center sm:justify-end gap-1"><Clock className="w-4 h-4" /> Collection {dueLabel(job.dueDate)}</div>
                <div className="text-xs text-gray-500 mt-1">{job.completedItems} of {job.items.length} items completed</div>
              </div>
            </div>
            <div className="divide-y divide-gray-100">
              {job.items.map(item => (
                <div key={item._id} className="px-4 sm:px-5 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-2">
                  <div className="min-w-0">
                    <div className="text-sm font-medium text-gray-900">{item.isCustomItem ? (item.customName || 'Custom item') : (item.serviceId?.name || 'Unknown service')} <span className="text-gray-400 font-normal">× {item.quantity}</span></div>
                    {item.specialInstructions && <div className="text-xs text-gray-500 mt-0.5">{item.specialInstructions}</div>}
                  </div>
                  <select value={item.status || 'received'} onChange={e => updateItemStatus(job, item, e.target.value as ItemStatus)} disabled={updatingItemId === item._id}
                    className={`w-full sm:w-52 px-3 py-2 text-sm font-medium rounded-lg border focus:ring-2 focus:ring-primary-500 disabled:opacity-50 ${item.status === 'completed' ? 'bg-green-50 border-green-200 text-green-700' : item.status === 'in_progress' ? 'bg-yellow-50 border-yellow-200 text-yellow-700' : item.status === 'issue_reported' || item.status === 'cancelled' ? 'bg-red-50 border-red-200 text-red-700' : item.status === 'ready' ? 'bg-purple-50 border-purple-200 text-purple-700' : item.status === 'out_for_delivery' ? 'bg-orange-50 border-orange-200 text-orange-700' : 'bg-gray-50 border-gray-200 text-gray-700'}`}>
                    {itemStatuses.map(status => <option key={status.value} value={status.value}>{status.label}</option>)}
                  </select>
                </div>
              ))}
            </div>
          </section>
        ))}
      </main>
    </div>
  );
};

export default PendingJobs;
